import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ActionRecord, InsightRecord } from "@graftus/schema";

import { useActions } from "@/hooks/use-actions";
import { DASHBOARD_TRPC_STALE_MS } from "@/lib/dashboard-trpc-stale";
import { useTRPC } from "@/trpc/client";
import type { AnserTip, AnserTipKind } from "./anser-tip";
import type { RailStarter } from "./rail-starter";
import { useRailStarters } from "./rail-starters";

/** How many tip cards the panel shows. */
export const ANSER_TIP_COUNT = 3;

/** Longer insight descriptions fall back to the title so a card stays one thought. */
const MAX_TIP_TEXT = 140;

export interface AnserTipInputs {
  insights: InsightRecord[];
  actions: ActionRecord[];
  starters: RailStarter[];
  /** On a member profile: that member's insights read as "individual". */
  memberId?: string | null;
  openTodoLabel: string;
}

function insightKind(insight: InsightRecord, memberId?: string | null): AnserTipKind {
  if (memberId && insight.memberId === memberId) return "individual";
  if (insight.accent === "danger") return "attention";
  if (insight.accent === "warning") return "risk";
  return "opportunity";
}

/**
 * Tips for the Anser panel (GRA-149), in priority order: one insight (typed
 * by accent, or "individual" on that member's profile), one open action, then
 * the rail starters as dialogue. Never more than three, never asserting a
 * fact the data does not back.
 */
export function buildAnserTips(inputs: AnserTipInputs): AnserTip[] {
  const out: AnserTip[] = [];
  const take = (tip: AnserTip) => {
    if (out.length < ANSER_TIP_COUNT) out.push(tip);
  };

  const insights = [...inputs.insights].sort((a, b) => {
    const rank = (i: InsightRecord) =>
      i.accent === "danger" ? 0 : i.accent === "warning" ? 1 : 2;
    return rank(a) - rank(b) || b.priority - a.priority;
  });
  const scoped = inputs.memberId
    ? insights.filter((i) => i.memberId === inputs.memberId)
    : insights;
  const insight = scoped[0] ?? insights[0];
  if (insight) {
    const description = insight.description?.trim() ?? "";
    take({
      id: `insight:${insight.id}`,
      kind: insightKind(insight, inputs.memberId),
      text:
        description && description.length <= MAX_TIP_TEXT
          ? description
          : insight.title,
      cta: insight.cta ? { label: insight.cta.label, to: insight.cta.to } : null,
      prompt: insight.suggestedPrompt ?? null,
      ai: true,
    });
  }

  const action = inputs.actions.find(
    (a) => a.status === "proposed" || a.status === "accepted",
  );
  if (action) {
    take({
      id: `action:${action.id}`,
      kind: "todo",
      text: action.title,
      cta: { label: inputs.openTodoLabel, to: "/" },
      prompt: action.title,
      ai: true,
    });
  }

  for (const starter of inputs.starters) {
    if (out.some((tip) => tip.id === starter.id)) continue;
    take({
      id: starter.id,
      kind: "dialogue",
      text: starter.title,
      cta: null,
      prompt: starter.prompt,
      ai: false,
    });
  }

  return out;
}

/**
 * Live tips for the panel. The insight list shares its query key with the
 * dashboard and the rail starters, so it is one fetch.
 */
export function useAnserTips(
  businessId: string | undefined,
  enabled: boolean,
  memberId?: string | null,
): AnserTip[] {
  const { t } = useTranslation("nav");
  const trpc = useTRPC();
  const id = enabled ? businessId : undefined;
  const insightsQuery = useQuery(
    trpc.intelligence.insights.list.queryOptions(
      { businessId: id ?? "" },
      { enabled: !!id, staleTime: DASHBOARD_TRPC_STALE_MS },
    ),
  );
  const { actions } = useActions(id);
  const starters = useRailStarters(businessId, enabled);
  const openTodoLabel = t("anser.openTodo");

  return useMemo(
    () =>
      buildAnserTips({
        insights: (insightsQuery.data as InsightRecord[] | undefined) ?? [],
        actions,
        starters,
        memberId,
        openTodoLabel,
      }),
    [insightsQuery.data, actions, starters, memberId, openTodoLabel],
  );
}
